import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useSelectedHospital } from "@/components/AppLayout";
import { useAuth } from "@/context/AuthContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend,
} from "recharts";
import {
  ChartLineUp, CheckCircle, Warning, Clock, Sparkle, ListChecks, Users, TrendUp, TrendDown,
} from "@phosphor-icons/react";

function statusPill(s) {
  return <span className={`status-pill status-${s}`}>{s.replace("_", " ")}</span>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const hospitalId = useSelectedHospital();
  const [kpis, setKpis] = useState(null);
  const [trend, setTrend] = useState([]);
  const [topClean, setTopClean] = useState([]);
  const [topImprove, setTopImprove] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const params = hospitalId ? { hospital_id: hospitalId } : {};
      const [k, t, tc, ti, r] = await Promise.all([
        api.get("/dashboard/kpis", { params }),
        api.get("/dashboard/trend", { params: { ...params, days: 7 } }),
        api.get("/dashboard/top-areas", { params: { ...params, direction: "clean", limit: 5 } }),
        api.get("/dashboard/top-areas", { params: { ...params, direction: "improve", limit: 5 } }),
        api.get("/dashboard/staff-ranking", { params: { ...params, limit: 5 } }),
      ]);
      setKpis(k.data); setTrend(t.data); setTopClean(tc.data); setTopImprove(ti.data); setRanking(r.data);
      setLoading(false);
    };
    load();
  }, [hospitalId]);

  if (loading || !kpis) {
    return <div className="text-slate-500 text-sm">Loading dashboard…</div>;
  }

  const kpiCards = [
    { label: "Cleaning Compliance", value: `${kpis.compliance_pct}%`, icon: Sparkle, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "SLA Adherence", value: `${kpis.sla_adherence_pct}%`, icon: CheckCircle, color: "text-green-600", bg: "bg-green-50" },
    { label: "Verification Pending", value: kpis.verification_pending, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Overdue + Escalated", value: kpis.overdue + kpis.escalated, icon: Warning, color: "text-red-600", bg: "bg-red-50" },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header>
        <div className="label-small text-blue-700">Operations Overview</div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>
          Welcome back, {user?.name?.split(" ")[0]}.
        </h1>
        <p className="text-sm text-slate-600 mt-1">Real-time housekeeping performance across your hospitals.</p>
      </header>

      {/* KPI tiles */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
        {kpiCards.map((k) => (
          <div key={k.label} className="kpi-tile flex items-center gap-4" data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className={`w-11 h-11 rounded-md ${k.bg} flex items-center justify-center`}>
              <k.icon size={22} className={k.color} weight="duotone" />
            </div>
            <div>
              <div className="label-small">{k.label}</div>
              <div className="text-3xl font-extrabold text-slate-900 mt-0.5" style={{ fontFamily: "Manrope" }}>{k.value}</div>
            </div>
          </div>
        ))}
      </section>

      {/* Status breakdown */}
      <section className="flat-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-small">Task Status Breakdown</div>
            <h3 className="text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>{kpis.total_tasks} tasks total</h3>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
          {[
            ["pending", kpis.pending], ["in_progress", kpis.in_progress], ["completed", kpis.completed],
            ["verified", kpis.verified], ["rejected", kpis.rejected], ["escalated", kpis.escalated], ["overdue", kpis.overdue],
          ].map(([k, v]) => (
            <div key={k} className="border border-slate-200 rounded-md p-3" data-testid={`status-${k}`}>
              <div className="label-small">{k.replace("_", " ")}</div>
              <div className="text-2xl font-extrabold text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>{v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Charts row */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="flat-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-small">7-Day Compliance Trend</div>
              <h3 className="text-lg font-bold text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>Daily compliance %</h3>
            </div>
            <ChartLineUp size={20} className="text-slate-400" />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trend} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={11} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#64748B" fontSize={11} domain={[0, 100]} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="compliance" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 3 }} name="Compliance %" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="flat-card p-6">
          <div className="label-small mb-1">Tasks per day</div>
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Volume</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={trend} margin={{ top: 12, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="date" stroke="#64748B" fontSize={11} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }} />
              <Bar dataKey="total" fill="#2563EB" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Top areas + Ranking */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="flat-card p-6">
          <div className="flex items-center gap-2 mb-3">
            <TrendUp size={18} className="text-green-600" weight="duotone" />
            <div className="label-small text-green-700">Top Clean Areas</div>
          </div>
          <ul className="space-y-2">
            {topClean.map((a, i) => (
              <li key={a.area_id} className="flex items-center justify-between text-sm" data-testid={`top-clean-${i}`}>
                <span className="text-slate-700 truncate pr-2">{i + 1}. {a.area_name}</span>
                <span className="font-bold text-green-700">{a.compliance}%</span>
              </li>
            ))}
            {topClean.length === 0 && <li className="text-sm text-slate-500">No data yet.</li>}
          </ul>
        </div>

        <div className="flat-card p-6">
          <div className="flex items-center gap-2 mb-3">
            <TrendDown size={18} className="text-red-600" weight="duotone" />
            <div className="label-small text-red-700">Needs Improvement</div>
          </div>
          <ul className="space-y-2">
            {topImprove.map((a, i) => (
              <li key={a.area_id} className="flex items-center justify-between text-sm" data-testid={`top-improve-${i}`}>
                <span className="text-slate-700 truncate pr-2">{i + 1}. {a.area_name}</span>
                <span className="font-bold text-red-700">{a.compliance}%</span>
              </li>
            ))}
            {topImprove.length === 0 && <li className="text-sm text-slate-500">No data yet.</li>}
          </ul>
        </div>

        <div className="flat-card p-6">
          <div className="flex items-center gap-2 mb-3">
            <Users size={18} className="text-blue-600" weight="duotone" />
            <div className="label-small text-blue-700">Staff Ranking</div>
          </div>
          <ul className="space-y-2">
            {ranking.map((r, i) => (
              <li key={r.user_id} className="flex items-center justify-between text-sm" data-testid={`ranking-${i}`}>
                <span className="text-slate-700 truncate pr-2">{i + 1}. {r.name}</span>
                <span className="font-bold text-blue-700">{r.compliance}%</span>
              </li>
            ))}
            {ranking.length === 0 && <li className="text-sm text-slate-500">No data yet.</li>}
          </ul>
        </div>
      </section>
    </div>
  );
}
